import { useLayoutEffect, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { SUBSCRIPTION_TIERS } from '../config/constants';
import { FiCheck, FiLoader, FiArrowRight } from 'react-icons/fi';
import {
  BrowserCard,
  PathwayDashboardMockup,
  PathwayDetailMockup,
  CurriculumTabSwitcher,
  CurriculumCategoriesView,
  FAQItem,
  catalogueResources,
  ResourceModalLoggedIn,
  faqs,
} from './HomeMockups';
import './Home.css';

/* ─────────────────────────────────────────────────────────────────────────────
   PLAN-AWARE PRICING SECTION
───────────────────────────────────────────────────────────────────────────── */
const LoggedInPricing = () => {
  const { userProfile } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const tier = userProfile?.subscription?.tier;
  const status = userProfile?.subscription?.status;
  const hasActiveSubscription = ['active', 'trialing'].includes(status);
  const isTrialing = status === 'trialing';
  const trialUsed = userProfile?.subscription?.trialUsed === true;
  // 14 days for teachers who signed up through a referral link
  const trialDays = userProfile?.referral?.trialDays || 7;

  const tierOrder = ['standard', 'premium', 'premium_bespoke'];
  const currentTierIndex = tierOrder.indexOf(tier);

  const handleUpdatePlan = async (priceId) => {
    try {
      setActionLoading(true);
      setError(null);
      const updateSub = httpsCallable(functions, 'updateSubscription');
      const result = await updateSub({ priceId });
      if (result.data.status === 'scheduled') {
        navigate('/subscription?downgrade_scheduled=true');
      } else {
        navigate('/subscription?success=true');
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      setError(err.message || 'Failed to update subscription. Please try again.');
      setActionLoading(false);
    }
  };

  const handleSubscribe = async (priceId) => {
    try {
      setActionLoading(true);
      setError(null);
      const createCheckout = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckout({
        priceId,
        successUrl: `${window.location.origin}/subscription?success=true`,
        cancelUrl: `${window.location.origin}/home`,
      });
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Failed to start checkout. Please try again.');
      setActionLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const createTrial = httpsCallable(functions, 'createTrialCheckoutSession');
      const result = await createTrial({
        successUrl: `${window.location.origin}/subscription?trial_started=true`,
        cancelUrl: `${window.location.origin}/home`,
      });
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Error creating trial checkout:', err);
      setError(err.message || 'Failed to start trial. Please try again.');
      setActionLoading(false);
    }
  };

  const getCtaForTier = (t) => {
    // School members' plans are managed by their school
    if (tier === 'business') return null;
    const tIndex = tierOrder.indexOf(t.id);
    if (!hasActiveSubscription) {
      return {
        label: actionLoading ? 'Redirecting...' : 'Subscribe',
        action: () => handleSubscribe(t.stripePriceId),
        className: t.id === 'premium_bespoke' ? 'hp-btn hp-btn-primary' : 'hp-btn hp-btn-outline',
      };
    }
    if (tIndex > currentTierIndex) {
      return {
        label: actionLoading ? 'Updating...' : 'Upgrade',
        action: () => handleUpdatePlan(t.stripePriceId),
        className: 'hp-btn hp-btn-primary',
      };
    }
    if (tIndex < currentTierIndex) {
      return {
        label: actionLoading ? 'Updating...' : 'Downgrade',
        action: () => handleUpdatePlan(t.stripePriceId),
        className: 'hp-btn hp-btn-outline',
      };
    }
    return null; // current plan — no CTA button
  };

  return (
    <section id="pricing" className="hp-section hp-pricing">
      <div className="hp-container">
        <div className="hp-section-header hp-fade-up">
          <span className="hp-section-tag">Your plan</span>
          <h2>
            {hasActiveSubscription
              ? <><em className="hp-teal">Manage</em> your subscription</>
              : <>Simple, transparent <em className="hp-teal">pricing</em></>
            }
          </h2>
          {hasActiveSubscription ? (
            <p>
              {isTrialing
                ? 'You are currently on a free trial. Upgrade to a paid plan at any time.'
                : 'You are subscribed below. Upgrade or downgrade at any time from here or your subscription page.'}
            </p>
          ) : (
            <p>All plans include the placement test and automated learning pathways. No setup fees. Cancel any time.</p>
          )}
        </div>

        {error && (
          <div className="hp-fade-up" style={{ maxWidth: '720px', margin: '0 auto 24px', backgroundColor: '#fef2f2', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {/* Free trial prompt for users with no subscription who haven't used their trial */}
        {!hasActiveSubscription && !trialUsed && (
          <div className="hp-fade-up" style={{ maxWidth: '720px', margin: '0 auto 32px', border: '2px solid rgba(14,41,84,0.3)', borderRadius: '12px', padding: '28px', textAlign: 'center', backgroundColor: '#fff' }}>
            <span style={{ display: 'inline-block', fontSize: '0.8rem', fontWeight: 700, padding: '4px 12px', borderRadius: '999px', marginBottom: '12px', backgroundColor: 'rgba(14,41,84,0.1)', color: '#0E2954' }}>
              New members
            </span>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: 'var(--navy)' }}>Start your {trialDays}-day free trial</h3>
            <p style={{ color: '#6b7280', marginBottom: '4px' }}>Full Standard plan access — no charge today.</p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '20px' }}>
              Card required. Cancel any time before day {trialDays} to avoid being charged.
            </p>
            <button
              onClick={handleStartTrial}
              disabled={actionLoading}
              className="hp-btn hp-btn-primary hp-btn-lg"
            >
              {actionLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Redirecting...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Start free trial
                  <FiArrowRight />
                </span>
              )}
            </button>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '12px' }}>
              Then £{SUBSCRIPTION_TIERS.STANDARD.price}/month unless cancelled
            </p>
          </div>
        )}

        <div className="hp-pricing-grid">
          {Object.values(SUBSCRIPTION_TIERS).map((t, idx) => {
            const isCurrent = hasActiveSubscription && t.id === tier;
            const cta = getCtaForTier(t);
            const isFeatured = t.id === 'premium';

            return (
              <div key={t.id} className={`hp-pricing-card-wrap hp-fade-up${idx > 0 ? ` hp-delay-${idx}` : ''}`}>
                {isFeatured && !isCurrent && (
                  <div className="hp-pricing-popular">Most popular</div>
                )}
                {isCurrent && (
                  <div className="hp-pricing-popular" style={{ backgroundColor: '#0E2954' }}>
                    {isTrialing ? 'Your trial plan' : 'Your current plan'}
                  </div>
                )}
                <div className={`hp-pricing-card${isFeatured ? ' hp-pricing-card--featured' : ''}${isCurrent ? ' hp-pricing-card--current' : ''}`}
                  style={isCurrent ? { outline: '2px solid #0E2954', outlineOffset: '2px' } : undefined}
                >
                  <div className="hp-pricing-name">{t.name}</div>
                  <div className="hp-pricing-price">
                    <span className="hp-pricing-amount">£{t.price}</span>
                    <span className="hp-pricing-period">/month</span>
                  </div>
                  <ul className="hp-pricing-features">
                    {t.features.map(f => (
                      <li key={f} className="hp-pricing-feature">{f}</li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Link
                      to="/subscription"
                      className="hp-btn hp-btn-outline hp-pricing-cta"
                    >
                      Manage subscription
                    </Link>
                  ) : cta ? (
                    <button
                      onClick={cta.action}
                      disabled={actionLoading}
                      className={`${cta.className} hp-pricing-cta`}
                      style={{ width: '100%', cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1 }}
                    >
                      {cta.label}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {hasActiveSubscription && (
          <div className="hp-pricing-footer hp-fade-up">
            <small>
              Upgrades take effect immediately. Downgrades take effect at the end of your current billing period.{' '}
              <Link to="/subscription" style={{ color: 'var(--teal)', textDecoration: 'underline' }}>
                Full subscription details →
              </Link>
            </small>
          </div>
        )}
        {!hasActiveSubscription && (
          <div className="hp-pricing-footer hp-fade-up">
            <small>The {trialDays}-day trial period is for the Standard membership only.</small>
          </div>
        )}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   LOGGED-IN HOME PAGE
───────────────────────────────────────────────────────────────────────────── */
const HomeLoggedIn = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [modalResource, setModalResource] = useState(null);
  const [curriculumFlipped, setCurriculumFlipped] = useState(false);

  /* Fade-up scroll animations */
  useEffect(() => {
    const fadeObs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('hp-visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.hp-fade-up').forEach(el => fadeObs.observe(el));
    return () => fadeObs.disconnect();
  }, []);

  return (
    <div className="hp-page hp-page--logged-in">
      <Helmet>
        <title>TeachESLPro - Your Dashboard Home</title>
        <meta name="description" content="TeachESLPro - placement tests and personalised learning pathways for ESL tutors." />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── SECTION 1: HERO ────────────────────────────────────────────── */}
      <section id="hero" className="hp-hero">
        <div className="hp-hero-inner">

          {/* Left: copy */}
          <div className="hp-hero-copy hp-fade-up">
            <div className="hp-hero-badge">For private &amp; freelance ESL tutors</div>
            <h1>
              From placement test to{' '}
              <em className="hp-teal">personalised pathway</em>
              {' '}- in minutes
            </h1>
            <p className="hp-hero-sub">
              TeachESLPro generates a complete, structured lesson pathway for every student - matched to their exact level, goals and interests. Spend less time planning and more time teaching.
            </p>
            <div className="hp-hero-ctas">
              <Link to="/dashboard" className="hp-btn hp-btn-primary hp-btn-lg">Go to dashboard</Link>
              <a href="#how-it-works" className="hp-btn hp-btn-outline hp-btn-lg"
                onClick={e => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>
                See how it works
              </a>
            </div>
            <div className="hp-hero-trust">
              <div className="hp-avatars">
                <span className="hp-avatar">S</span>
                <span className="hp-avatar">M</span>
                <span className="hp-avatar">L</span>
                <span className="hp-avatar">R</span>
              </div>
              Trusted by hundreds of ESL teachers worldwide
            </div>
          </div>

          {/* Right: screenshots */}
          <div className="hp-hero-visual hp-fade-up hp-delay-2">
            <div className="hp-hero-screens">
              <div style={{ position: 'relative' }}>
                <div className="hp-hero-pill hp-hero-pill--top">📊 Placement results ready</div>
                <BrowserCard url="teacheslpro.com/assessment">
                  <img
                    src="/images/Assessment result.png"
                    alt="Assessment Complete screen"
                    style={{ width: '100%', display: 'block', borderRadius: '4px' }}
                  />
                </BrowserCard>
              </div>
              <div style={{ position: 'relative' }}>
                <div className="hp-hero-pill hp-hero-pill--bottom">⚡ Pathway auto-generated · 96 lessons</div>
                <PathwayDetailMockup />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── SECTION 2: PROOF BAR ──────────────────────────────────────── */}
      <div className="hp-proof-bar">
        <div className="hp-proof-bar-inner">
          {[
            'Hundreds of ESL teachers',
            '30+ countries',
            'New resources every month',
            'Bespoke lessons in 72 hours',
            '7-day free trial',
          ].map((item, i, arr) => (
            <span key={item} style={{ display: 'contents' }}>
              <span className="hp-proof-item">{item}</span>
              {i < arr.length - 1 && <span className="hp-proof-dot" />}
            </span>
          ))}
        </div>
      </div>

      {/* ── SECTION 3: HOW IT WORKS ───────────────────────────────────── */}
      <section id="how-it-works" className="hp-section hp-hiw">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">How it works</span>
            <h2>A structured pathway for <em className="hp-teal">every student</em></h2>
            <p>Five steps from a blank slate to a fully personalised, resource-linked lesson plan.</p>
          </div>

          <div className="hp-hiw-steps">
            {[
              { icon: '📩', title: 'Send placement test',   desc: 'Share a link with your student. They complete reading, writing & listening - independently, in their own time.' },
              { icon: '📊', title: 'Review skill levels',   desc: 'Receive a clear level breakdown for each skill. Add your own assessment of speaking and comprehension.' },
              { icon: '🎯', title: 'Set goals & schedule',  desc: "Add the student's goals, interests, lessons per week and target duration. More context = better pathway." },
              { icon: '⚡', title: 'Pathway generated',     desc: 'A full lesson pathway is built automatically, with overviews for every lesson and catalogue resources linked in.' },
              { icon: '✏️', title: 'Teach & adapt',         desc: 'Edit lessons, request bespoke resources and track progress as your student advances through the pathway.' },
            ].map((step, i) => (
              <div key={step.title} className={`hp-hiw-step hp-fade-up hp-delay-${i + 1}`}>
                <div className="hp-hiw-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 4: STUDENT MANAGEMENT SHOWCASE ────────────────────── */}
      <section id="student-management" className="hp-section hp-showcase">
        <div className="hp-showcase-inner hp-container">
          <div className="hp-showcase-copy hp-fade-up">
            <span className="hp-section-tag">Student management</span>
            <h2>All your students, <em className="hp-teal">one place</em></h2>
            <p>See every student's pathway, level, progress and lesson schedule at a glance. No spreadsheets. No guesswork.</p>
            <div className="hp-bullets">
              {[
                { icon: '👁️', title: 'Instant progress overview', desc: 'See how far each student has progressed through their pathway - lessons completed, percentage done, lessons per week.' },
                { icon: '🔍', title: 'Filter by status',           desc: 'Quickly filter active, completed and draft pathways. Search by student name or level to find who you need fast.' },
                { icon: '➕', title: 'Add a new pathway in seconds', desc: 'Start a new student pathway directly from the dashboard. The system does the heavy lifting from there.' },
              ].map(b => (
                <div key={b.title} className="hp-bullet">
                  <div className="hp-bullet-icon">{b.icon}</div>
                  <div className="hp-bullet-text">
                    <strong>{b.title}</strong>
                    <span>{b.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hp-showcase-visual hp-fade-up hp-delay-2">
            <PathwayDashboardMockup />
          </div>
        </div>
      </section>

      {/* ── SECTION 5: INDIVIDUAL PATHWAY SHOWCASE ────────────────────── */}
      <section id="pathway-detail" className="hp-section hp-showcase-alt">
        <div className="hp-showcase-inner hp-container hp-showcase-inner--reversed">
          <div className="hp-showcase-visual hp-fade-up">
            <div className="hp-flip-wrapper">
              <div className={`hp-flip-inner${curriculumFlipped ? ' hp-flipped' : ''}`}>
                <div className="hp-flip-front">
                  <CurriculumTabSwitcher />
                </div>
                <div
                  className="hp-flip-back"
                  role="button"
                  aria-label="Show pathway view"
                  tabIndex={curriculumFlipped ? 0 : -1}
                  onClick={() => setCurriculumFlipped(false)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurriculumFlipped(false);
                    }
                  }}
                >
                  <CurriculumCategoriesView />
                </div>
              </div>
            </div>
            <button
              className="hp-flip-hint-btn"
              onClick={() => setCurriculumFlipped(f => !f)}
              aria-pressed={curriculumFlipped}
              aria-label={curriculumFlipped ? 'Show pathway view' : 'Explore curriculum categories'}
            >
              {curriculumFlipped ? '← Back to pathway view' : '↻ Explore categories view'}
            </button>
          </div>
          <div className="hp-showcase-copy hp-fade-up hp-delay-2">
            <span className="hp-section-tag">Individual pathway</span>
            <h2>A full curriculum, <em className="hp-teal">built for one</em></h2>
            <p>Generate a structured pathway for your students, organised into meaningful stages with progress tracked every step of the way.</p>
            <div className="hp-bullets">
              {[
                { icon: '🗂️', title: 'Staged progression',           desc: 'Lessons grouped into Foundation, Skill Expansion, Controlled Fluency and Independent Performance - each with its own progress tracker and lesson count.' },
                { icon: '📈', title: 'Overall progress ring',         desc: 'A visual indicator shows exactly where the student is across their entire pathway - current level to target level.' },
                { icon: '🔗', title: 'Linked resources & collections', desc: 'Resources from the TeachESLPro catalogue are linked directly into the pathway. Teachers can remove linked resources, add resources and request bespoke resources from each lesson.' },
              ].map(b => (
                <div key={b.title} className="hp-bullet">
                  <div className="hp-bullet-icon">{b.icon}</div>
                  <div className="hp-bullet-text">
                    <strong>{b.title}</strong>
                    <span>{b.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: FEATURES GRID ──────────────────────────────────── */}
      <section id="features" className="hp-section hp-features">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">Full feature set</span>
            <h2>Everything a private tutor <em className="hp-teal">actually needs</em></h2>
          </div>

          <div className="hp-features-grid">
            <div className="hp-feature-card hp-fade-up">
              <div className="hp-feature-icon">📋</div>
              <h3>Multi-skill Placement Test</h3>
              <p>Send students a single link. They complete a structured test covering reading, writing and listening. You receive a clear level breakdown for each skill - then add your own speaking and comprehension assessment.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">All plans</span>
            </div>

            <div className="hp-feature-card hp-fade-up hp-delay-1">
              <div className="hp-feature-icon">🗂️</div>
              <h3>Growing Resource Catalogue</h3>
              <p>A library of professionally designed, human-made ESL resources, with new resources added every month.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">All plans</span>
            </div>

            <div className="hp-feature-card hp-feature-card--full hp-fade-up hp-delay-2">
              <div className="hp-feature-inner-grid">
                <div>
                  <div className="hp-feature-icon">⚡</div>
                  <h3>Automated Learning Pathways</h3>
                  <p>Based on placement results, teacher input, student goals, interests, lesson frequency and target duration, a full staged lesson pathway is generated automatically. Each lesson includes an overview and, where a matching catalogue resource exists, it is linked automatically.</p>
                  <span className="hp-feature-badge hp-feature-badge--accent">Unique to TeachESLPro</span>
                </div>
                <div className="hp-feature-checklist">
                  {[
                    'Reading, writing, listening from placement',
                    'Teacher-assessed speaking level',
                    'Student goals & target level',
                    'Interests & topic preferences',
                    'Lessons per week & timeframe',
                    'Auto-matched & editable resources',
                  ].map(item => (
                    <div key={item} className="hp-feature-check">{item}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hp-feature-card hp-fade-up hp-delay-3">
              <div className="hp-feature-icon">✍️</div>
              <h3>Bespoke Lesson Requests</h3>
              <p>Need something specific? Request a custom resource directly from a lesson overview in your student's pathway or from your dashboard. TeachESLPro delivers a professionally designed resource tailored to your request within 72 hours.</p>
              <span className="hp-feature-badge hp-feature-badge--accent">Premium Bespoke plan</span>
            </div>

            <div className="hp-feature-card hp-fade-up hp-delay-4">
              <div className="hp-feature-icon">📁</div>
              <h3>Organise &amp; Manage Resources</h3>
              <p>Save, organise and manage your downloaded resources. Link collections directly to a student's pathway so everything is in one place.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">Premium &amp; above</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: TESTIMONIALS ───────────────────────────────────── */}
      <section id="about" className="hp-section hp-testimonials">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">What teachers say</span>
            <h2>Real feedback from <em className="hp-teal">real teachers</em></h2>
          </div>

          <div className="hp-testimonials-grid">
            <div className="hp-testimonial hp-testimonial--featured hp-fade-up">
              <div className="hp-testimonial-stars">★★★★★</div>
              <p className="hp-testimonial-text">
                "I used to spend hours every week planning from scratch for each student. The pathway system changed everything - I set it up once and have a structured plan ready to go. My students have noticed how much more organised the lessons feel."
              </p>
              <div className="hp-testimonial-author">Sarah J.</div>
              <div className="hp-testimonial-role">Freelance ESL tutor · 14 students</div>
            </div>

            <div className="hp-testimonial hp-fade-up hp-delay-1">
              <div className="hp-testimonial-stars">★★★★★</div>
              <p className="hp-testimonial-text">
                "The placement test alone is worth the subscription. I used to guess levels from a quick conversation. Now I have a proper breakdown before the first lesson even starts."
              </p>
              <div className="hp-testimonial-author">Marco A.</div>
              <div className="hp-testimonial-role">Private tutor · Online, Italy</div>
            </div>

            <div className="hp-testimonial hp-fade-up hp-delay-2">
              <div className="hp-testimonial-stars">★★★★★</div>
              <p className="hp-testimonial-text">
                "The bespoke request feature is brilliant. I needed something tailored for a student preparing for a very specific industry interview. It arrived within 72 hours and was exactly right."
              </p>
              <div className="hp-testimonial-author">Laura P.</div>
              <div className="hp-testimonial-role">ESL teacher · London</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: PLAN-AWARE PRICING ────────────────────────────── */}
      <LoggedInPricing />

      {/* ── SECTION: COMPARE PLAN FEATURES ─────────────────────────────── */}
      <section className="hp-compare-section">
        <div className="hp-container">
          <h2 className="hp-compare-heading hp-fade-up">Compare plan features</h2>

          <div className="hp-compare-card hp-fade-up hp-delay-1">
            <div className="hp-compare-scroll-wrap">
              <table className="hp-compare-table">
                <thead>
                  <tr>
                    <th className="hp-compare-th hp-compare-th--feature">Features</th>
                    <th className="hp-compare-th">Standard</th>
                    <th className="hp-compare-th">Premium</th>
                    <th className="hp-compare-th">Premium Bespoke</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Access the catalogue and download resources', standard: true,  premium: true,  bespoke: true  },
                    { label: 'Automated learning pathways',                  standard: true,  premium: true,  bespoke: true  },
                    { label: 'Unlimited downloads',                          standard: false, premium: true,  bespoke: true  },
                    { label: 'Organise and manage saved resources',          standard: false, premium: true,  bespoke: true  },
                    { label: 'Custom resource requests',                     standard: false, premium: false, bespoke: true  },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'hp-compare-row--white' : 'hp-compare-row--grey'}>
                      <td className="hp-compare-td hp-compare-td--feature">{row.label}</td>
                      {['standard', 'premium', 'bespoke'].map(key => (
                        <td key={key} className="hp-compare-td hp-compare-td--cell">
                          {row[key] ? (
                            <span className="hp-compare-tick" aria-label="Included">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M4 10.5L8.5 15L16 6" stroke="#0E2954" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          ) : (
                            <span className="hp-compare-cross" aria-label="Not included">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M6 6L14 14M14 6L6 14" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 9: SAMPLE RESOURCES ───────────────────────────────── */}
      <section id="resources" className="hp-section hp-resources">
        <div className="hp-container">
          <div className="hp-resources-header hp-fade-up">
            <div className="hp-resources-header-left">
              <span className="hp-section-tag">Sample resources</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,2.5vw,2.2rem)', fontWeight: 600, color: 'var(--navy)', margin: '8px 0 0', lineHeight: 1.25 }}>
                See what's in the <em className="hp-teal">catalogue</em>
              </h2>
            </div>
            <Link to="/resources" className="hp-resources-browse">Browse all resources →</Link>
          </div>

          <div className="hp-resources-grid">
            {catalogueResources.map((r, i) => (
              <div
                key={r.title}
                className={`hp-resource-card hp-fade-up hp-delay-${i}`}
                onClick={() => setModalResource(r)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setModalResource(r); }}
              >
                <div
                  className={`hp-resource-thumb hp-resource-thumb--${r.thumb}`}
                  style={r.image ? { backgroundImage: `url(${r.image})`, backgroundSize: 'cover', backgroundPosition: 'top center' } : undefined}
                >
                  {!r.image && r.icon}
                  <span className="hp-resource-level">{r.level}</span>
                </div>
                <div className="hp-resource-body">
                  <div className="hp-resource-title">{r.title}</div>
                  <div className="hp-resource-skills">
                    {r.skills.map(s => (
                      <span key={s} className="hp-resource-skill-pill">{s}</span>
                    ))}
                  </div>
                  <span className="hp-resource-preview-label">👁 Preview</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 10: FAQ ───────────────────────────────────────────── */}
      <section id="faq" className="hp-section hp-faq">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">FAQs</span>
          </div>

          <div className="hp-faq-list hp-fade-up">
            {faqs.map((item, i) => (
              <FAQItem
                key={i}
                q={item.q}
                a={item.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 11: FINAL CTA ─────────────────────────────────────── */}
      <section id="trial" className="hp-final-cta">
        <div className="hp-fade-up">
          <h2>
            Ready to teach with <em style={{ fontStyle: 'italic', color: '#4FB0B1' }}>purpose?</em>
          </h2>
          <p>Save hours of planning time and increase student satisfaction with TeachESLPro.</p>
          <div className="hp-final-cta-btns">
            <Link to="/dashboard" className="hp-btn hp-btn-white hp-btn-lg">Go to dashboard</Link>
            <a
              href="#pricing"
              className="hp-btn hp-btn-white-outline hp-btn-lg"
              onClick={e => {
                e.preventDefault();
                const el = document.getElementById('pricing');
                if (el) {
                  const offset = 80;
                  const top = el.getBoundingClientRect().top + window.scrollY - offset;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}
            >View pricing</a>
          </div>
        </div>
      </section>

      {/* ── PREVIEW MODAL ─────────────────────────────────────────────── */}
      {modalResource && (
        <ResourceModalLoggedIn resource={modalResource} onClose={() => setModalResource(null)} />
      )}

    </div>
  );
};

export default HomeLoggedIn;
